import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import "./EmployeeList.css";
// const url = import.meta.env.VITE_BACKEND_URL;
const url = process.env.REACT_APP_BACKEND_URL;

const EmployeeList = () => {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" });
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("personal");
  const [formData, setFormData] = useState({});
  const [departments, setDepartments] = useState([]);
  const [employeeTypes, setEmployeeTypes] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchEmployees();
    fetchDepartmentsAndTypes();
  }, []);

  useEffect(() => {
    filterEmployees();
  }, [searchTerm, employees]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${url}/api/employee`);
      setEmployees(response.data.data || response.data);
      setFilteredEmployees(response.data.data || response.data);

    } catch (err) {
      console.error("Error fetching employees:", err);
      setError("Failed to fetch employees. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartmentsAndTypes = async () => {
    try {
      const [deptResponse, typeResponse] = await Promise.all([
        axios.get(`${url}/api/department`),
        axios.get(`${url}/api/employeetype`)
      ]);

      // Extract arrays from API responses
      const extractArrayFromResponse = (responseData) => {
        if (Array.isArray(responseData)) return responseData;
        if (responseData && Array.isArray(responseData.data)) return responseData.data;
        if (responseData && responseData.data && Array.isArray(responseData.data.result)) return responseData.data.result;
        if (responseData && responseData.result && Array.isArray(responseData.result)) return responseData.result;
        return [];
      };

      setDepartments(extractArrayFromResponse(deptResponse.data));
      setEmployeeTypes(extractArrayFromResponse(typeResponse.data));
    } catch (err) {
      console.error("Error fetching departments and employee types:", err);
    }
  };

  const filterEmployees = () => {
    if (!searchTerm) {
      setFilteredEmployees(employees);
      return;
    }

    const filtered = employees.filter(
      (employee) =>
        employee.fullname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.contract_type?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredEmployees(filtered);
  };

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });

    const sortedEmployees = [...filteredEmployees].sort((a, b) => {
      let aValue, bValue;

      if (key.includes(".")) {
        const keys = key.split(".");
        aValue = keys.reduce((obj, k) => obj && obj[k], a);
        bValue = keys.reduce((obj, k) => obj && obj[k], b);
      } else {
        aValue = a[key];
        bValue = b[key];
      }

      if (aValue == null) aValue = "";
      if (bValue == null) bValue = "";

      if (aValue < bValue) return direction === "ascending" ? -1 : 1;
      if (aValue > bValue) return direction === "ascending" ? 1 : -1;
      return 0;
    });

    setFilteredEmployees(sortedEmployees);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString().split("T")[0];
  };

  const formatSalary = (salary) => {
    if (!salary) return "-";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(salary);
  };

  const getDepartmentName = (facultyId) => {
    const department = departments.find(dept => dept._id === facultyId);
    return department ? department.name : "Unknown Department";
  };

  const getEmployeeTypeName = (typeId) => {
    const employeeType = employeeTypes.find(type => type._id === typeId);
    return employeeType ? employeeType.name : "Unknown Type";
  };

  const viewEmployeeDetails = (employee) => {
    setSelectedEmployee(employee);
    setIsEditing(false);
    setFormData({});
    setIsModalOpen(true);
    setActiveTab("personal");
  };

  const startEditing = (employee) => {
    setSelectedEmployee(employee);
    setFormData({ ...employee });
    setIsEditing(true);
    setIsModalOpen(true);
    setActiveTab("personal");
  };

  const handleInputChange = (e, section = null, index = null) => {
    const { name, value } = e.target;

    if (section === "education" && index !== null) {
      // Handle education array
      const updatedEducation = [...formData.education];
      updatedEducation[index] = {
        ...updatedEducation[index],
        [name]: value
      };

      setFormData({
        ...formData,
        education: updatedEducation
      });
    } else if (section) {
      setFormData({
        ...formData,
        [section]: { ...formData[section], [name]: value },
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const saveEmployee = async () => {
    try {
      await axios.put(`${url}/api/employee/${formData._id}`, formData);
      const updatedEmployees = employees.map((emp) =>
        emp._id === formData._id ? formData : emp
      );
      setEmployees(updatedEmployees);
      setFilteredEmployees(updatedEmployees);
      setIsModalOpen(false);
      setIsEditing(false);
      setError("");
    } catch (err) {
      console.error("Error updating employee:", err);
      setError("Failed to update employee.");
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedEmployee(null);
    setIsEditing(false);
  };

  const deleteEmployee = async (id) => {
    if (window.confirm("Are you sure you want to delete this employee?")) {
      try {
        await axios.delete(`${url}/api/employee/${id}`);
        setEmployees(employees.filter((emp) => emp._id !== id));
        setFilteredEmployees(filteredEmployees.filter((emp) => emp._id !== id));
        setError("");
      } catch (err) {
        console.error("Error deleting employee:", err);
        setError("Failed to delete employee.");
      }
    }
  };

  const renderModalContent = () => {
    if (!selectedEmployee) return null;

    if (isEditing) {
      return (
        <div className="modal-content">
          <div className="modal-header">
            <h2>Edit Employee</h2>
          </div>

          <div className="modal-tabs">
            <button
              className={activeTab === "personal" ? "tab-active" : ""}
              onClick={() => setActiveTab("personal")}
            >
              Personal
            </button>
            <button
              className={activeTab === "education" ? "tab-active" : ""}
              onClick={() => setActiveTab("education")}
            >
              Education
            </button>
            <button
              className={activeTab === "employment" ? "tab-active" : ""}
              onClick={() => setActiveTab("employment")}
            >
              Employment
            </button>
          </div>

          <div className="tab-content">
            {activeTab === "personal" && (
              <div className="edit-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Full Name</label>
                    <input
                      type="text"
                      name="fullname"
                      value={formData.fullname || ""}
                      onChange={handleInputChange}
                      placeholder="Full Name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email || ""}
                      onChange={handleInputChange}
                      placeholder="Email"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="text"
                      name="phone"
                      value={formData.phone || ""}
                      onChange={handleInputChange}
                      placeholder="Phone"
                    />
                  </div>
                  <div className="form-group">
                    <label>Date of Birth</label>
                    <input
                      type="date"
                      name="dob"
                      value={formatDateForInput(formData.dob) || ""}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Gender</label>
                    <select
                      name="gender"
                      value={formData.gender || ""}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Marital Status</label>
                    <select
                      name="marital_status"
                      value={formData.marital_status || ""}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Status</option>
                      <option value="Single">Single</option>
                      <option value="Married">Married</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Blood Type</label>
                    <select
                      name="blood"
                      value={formData.blood || ""}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Blood Type</option>
                      <option value="O+">O+</option>
                      <option value="A+">A+</option>
                      <option value="B+">B+</option>
                      <option value="AB+">AB+</option>
                      <option value="O-">O-</option>
                      <option value="A-">A-</option>
                      <option value="B-">B-</option>
                      <option value="AB-">AB-</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Place of Birth</label>
                    <input
                      type="text"
                      name="pob"
                      value={formData.pob || ""}
                      onChange={handleInputChange}
                      placeholder="Place of Birth"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Nationality</label>
                    <input
                      type="text"
                      name="nationality"
                      value={formData.nationality || ""}
                      onChange={handleInputChange}
                      placeholder="Nationality"
                    />
                  </div>
                  <div className="form-group">
                    <label>Next of Kin Name</label>
                    <input
                      type="text"
                      name="next_of_kin_name"
                      value={formData.next_of_kin_name || ""}
                      onChange={handleInputChange}
                      placeholder="Next of Kin Name"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Next of Kin Contact</label>
                    <input
                      type="text"
                      name="next_of_kin_contact"
                      value={formData.next_of_kin_contact || ""}
                      onChange={handleInputChange}
                      placeholder="Next of Kin Contact"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === "education" && (
              <div className="edit-form">
                {formData.education && formData.education.map((edu, index) => (
                  <div key={index} className="education-item">
                    <h3>Education #{index + 1}</h3>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Degree</label>
                        <input
                          type="text"
                          name="degree"
                          value={edu.degree || ""}
                          onChange={(e) => handleInputChange(e, "education", index)}
                          placeholder="Degree"
                        />
                      </div>
                      <div className="form-group">
                        <label>Institution</label>
                        <input
                          type="text"
                          name="institution"
                          value={edu.institution || ""}
                          onChange={(e) => handleInputChange(e, "education", index)}
                          placeholder="Institution"
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Field of Study</label>
                        <input
                          type="text"
                          name="field"
                          value={edu.field || ""}
                          onChange={(e) => handleInputChange(e, "education", index)}
                          placeholder="Field of Study"
                        />
                      </div>
                      <div className="form-group">
                        <label>Date Graduated</label>
                        <input
                          type="date"
                          name="dateGraduated"
                          value={formatDateForInput(edu.dateGraduated) || ""}
                          onChange={(e) => handleInputChange(e, "education", index)}
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>GPA</label>
                        <input
                          type="text"
                          name="gpa"
                          value={edu.gpa || ""}
                          onChange={(e) => handleInputChange(e, "education", index)}
                          placeholder="GPA"
                        />
                      </div>
                      <div className="form-group">
                        <label>Grade</label>
                        <input
                          type="text"
                          name="grade"
                          value={edu.grade || ""}
                          onChange={(e) => handleInputChange(e, "education", index)}
                          placeholder="Grade"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === "employment" && (
              <div className="edit-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Joined Date</label>
                    <input
                      type="date"
                      name="joined_date"
                      value={formatDateForInput(formData.joined_date) || ""}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Obtained Date</label>
                    <input
                      type="date"
                      name="obtained_date"
                      value={formatDateForInput(formData.obtained_date) || ""}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Contract Type</label>
                    <select
                      name="contract_type"
                      value={formData.contract_type || ""}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Contract Type</option>
                      <option value="Full Time">Full Time</option>
                      <option value="Part Time">Part Time</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Employee Type</label>
                    <select
                      name="employee_type_id"
                      value={formData.employee_type_id || ""}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Employee Type</option>
                      {employeeTypes.map(type => (
                        <option key={type._id} value={type._id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Department</label>
                    <select
                      name="faculty_id"
                      value={formData.faculty_id || ""}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Department</option>
                      {departments.map(dept => (
                        <option key={dept._id} value={dept._id}>
                          {dept.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Salary</label>
                    <input
                      type="number"
                      name="salary"
                      value={formData.salary || ""}
                      onChange={handleInputChange}
                      placeholder="Salary"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button className="save-btn" onClick={saveEmployee}>
              Save Changes
            </button>
            <button className="cancel-btn" onClick={closeModal}>
              Cancel
            </button>
          </div>
        </div>
      );
    }

    // View mode (not editing)
    return (
      <div className="modal-content">
        <div className="employee-header">
          <div className="employee-avatar-large">
            {selectedEmployee.fullname ? selectedEmployee.fullname.charAt(0).toUpperCase() : 'E'}
          </div>
          <div className="employee-title">
            <h2>{selectedEmployee.fullname}</h2>
            <p>{selectedEmployee.contract_type || 'No position specified'}</p>
          </div>
        </div>

        <div className="modal-tabs">
          <button
            className={activeTab === "personal" ? "tab-active" : ""}
            onClick={() => setActiveTab("personal")}
          >
            Personal
          </button>
          <button
            className={activeTab === "education" ? "tab-active" : ""}
            onClick={() => setActiveTab("education")}
          >
            Education
          </button>
          <button
            className={activeTab === "employment" ? "tab-active" : ""}
            onClick={() => setActiveTab("employment")}
          >
            Employment
          </button>
        </div>

        <div className="tab-content">
          {activeTab === "personal" && (
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Full Name:</span>
                <span className="info-value">{selectedEmployee.fullname || '-'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Email:</span>
                <span className="info-value">{selectedEmployee.email || '-'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Phone:</span>
                <span className="info-value">{selectedEmployee.phone || '-'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Date of Birth:</span>
                <span className="info-value">{formatDate(selectedEmployee.dob)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Gender:</span>
                <span className="info-value">{selectedEmployee.gender || '-'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Marital Status:</span>
                <span className="info-value">{selectedEmployee.marital_status || '-'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Place of Birth:</span>
                <span className="info-value">{selectedEmployee.pob || '-'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Nationality:</span>
                <span className="info-value">{selectedEmployee.nationality || '-'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Blood Type:</span>
                <span className="info-value">{selectedEmployee.blood || '-'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Next of Kin Name:</span>
                <span className="info-value">{selectedEmployee.next_of_kin_name || '-'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Next of Kin Contact:</span>
                <span className="info-value">{selectedEmployee.next_of_kin_contact || '-'}</span>
              </div>
            </div>
          )}

          {activeTab === "education" && (
            <div className="info-grid">
              {selectedEmployee.education && selectedEmployee.education.length > 0 ? (
                selectedEmployee.education.map((edu, index) => (
                  <div key={index} className="education-section">
                    <h4>Education #{index + 1}</h4>
                    <div className="info-item">
                      <span className="info-label">Degree:</span>
                      <span className="info-value">{edu.degree || '-'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Institution:</span>
                      <span className="info-value">{edu.institution || '-'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Field of Study:</span>
                      <span className="info-value">{edu.field || '-'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Date Graduated:</span>
                      <span className="info-value">{formatDate(edu.dateGraduated)}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">GPA:</span>
                      <span className="info-value">{edu.gpa || '-'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Grade:</span>
                      <span className="info-value">{edu.grade || '-'}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-data">No education information available</div>
              )}
            </div>
          )}

          {activeTab === "employment" && (
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Joined Date:</span>
                <span className="info-value">{formatDate(selectedEmployee.joined_date)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Obtained Date:</span>
                <span className="info-value">{formatDate(selectedEmployee.obtained_date)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Contract Type:</span>
                <span className="info-value">{selectedEmployee.contract_type || '-'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Employee Type:</span>
                <span className="info-value">{getEmployeeTypeName(selectedEmployee.employee_type_id)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Department:</span>
                <span className="info-value">{getDepartmentName(selectedEmployee.faculty_id)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Salary:</span>
                <span className="info-value">{formatSalary(selectedEmployee.salary)}</span>
              </div>
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button className="edit-mode-btn" onClick={() => startEditing(selectedEmployee)}>
            Edit Information
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="employee-list-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading employees...</p>
        </div>
      </div>
    );
  }

  if (error && !isModalOpen) {
    return (
      <div className="employee-list-container">
        <div className="error-message">{error}</div>
        <button onClick={fetchEmployees} className="retry-btn">Retry</button>
      </div>
    );
  }

  return (
    <div className="employee-list-container">
      <h1>Employee Directory List</h1>

      {error && <div className="error-message">{error}</div>}

      <div className="controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search employees by name, email, or contract type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <span className="search-icon">🔍</span>
        </div>
        <button
          onClick={() => navigate("/employee")}
          style={{
            backgroundColor: "#3498db",
            color: "white",
            padding: "10px 20px",
            marginBottom: "20px",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
          }}
        >
          + Add New Employee
        </button>
      </div>

      {filteredEmployees.length === 0 ? (
        <div className="no-results">
          {searchTerm ? 'No employees match your search.' : 'No employees found.'}
        </div>
      ) : (
        <div className="table-container">
          <table className="employee-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('fullname')}>
                  Employee {sortConfig.key === 'fullname' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('email')}>
                  Contact {sortConfig.key === 'email' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('contract_type')}>
                  Position {sortConfig.key === 'contract_type' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('salary')}>
                  Salary {sortConfig.key === 'salary' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.map((employee) => (
                <tr key={employee._id}>
                  <td>
                    <div className="employee-info">
                      <div className="">
                        {employee.photo ? (
                          <img
                            src={`${url}/uploads/${employee.photo}`}
                            alt={employee.fullname}
                            width="50"
                            height="50"
                            style={{ borderRadius: "50%" }}
                          />
                        ) : (
                          <span>
                            {employee.fullname
                              ? employee.fullname.charAt(0).toUpperCase()
                              : "E"}
                          </span>
                        )}
                      </div>
                      <div className="name-details">
                        <div className="name">{employee.fullname}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="contact-info">
                      <div className="email">{employee.email}</div>
                      <div className="phone">{employee.phone}</div>
                    </div>
                  </td>
                  <td>
                    <div className="position-info">
                      <div className="role">{employee.contract_type || "-"}</div>
                      <div className="type">
                        {getEmployeeTypeName(employee.employee_type_id)}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="salary-info">
                      {formatSalary(employee.salary)}
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="view-btn"
                        onClick={() => viewEmployeeDetails(employee)}
                      >
                        View
                      </button>

                      <button
                        className="edit-btn"
                        onClick={() => startEditing(employee)}
                      >
                        Edit
                      </button>

                      {/* <button
                        className="delete-btn"
                        onClick={() => deleteEmployee(employee._id)}
                      >
                        Delete
                      </button> */}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>

          </table>
        </div>
      )}

      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>×</button>
            {renderModalContent()}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeList;